import type { Message } from '../../types/conversation';
import { formatDateTime } from '../../utils/format';
import type { BeijingDateTimeText } from '../../utils/format';
import { isExternalWeComUserId } from '../../utils/wecomIds';
import {
  extractWeComDisplayInfo,
  getSenderOpenDataId,
  replaceWeChatEmoticons,
  WECOM_NATIVE_DISPLAY_TYPES,
  WECOM_PREVIEWABLE_TYPES,
} from './MessageBubble';

/**
 * 所有企业微信会话展示页面共用的消息适配结果。
 * 仅当消息同时具备 msgid 与 secretKey 时，才交由 ww-open-message 渲染。
 */
export type WeComConversationDisplayMessage = {
  key: string;
  isSelf: boolean;
  rowClass: string;
  mainClass: string;
  bubbleClass: string;
  senderOpenId: string;
  senderNameType: 'userName' | 'externalUserName';
  avatarType: 'userAvatar' | 'externalUserAvatar';
  showAvatarOpenData: boolean;
  senderFallback: string;
  avatarInitial: string;
  msgid: string;
  secretKey: string;
  native: boolean;
  previewable: boolean;
  displayType: 'normal' | 'text';
  fallback: string;
  sendTime: BeijingDateTimeText | '';
};

const looksLikeMachineId = (value?: string | null) => {
  const text = String(value || '').trim();
  return !text || /^\d+$/.test(text) || /^(wo|wm|wr|wb)/.test(text) || /^[a-zA-Z0-9_-]{20,}$/.test(text);
};

const getSenderFallback = (message: Message) => {
  const name = String(message.sender_name || '').trim();
  if (name && !looksLikeMachineId(name)) return name;
  if (message.sender_type === 'external') return '外部联系人';
  if (message.sender_type === 'employee') return '员工';
  if (message.sender_type === 'robot') return '机器人';
  return '未知成员';
};

export const toWeComConversationDisplayMessage = (
  message: Message,
  index: number,
): WeComConversationDisplayMessage => {
  const isSelf = message.direction === 'send';
  const senderOpenId = getSenderOpenDataId(message);
  const senderId = senderOpenId;
  const displayInfo = extractWeComDisplayInfo(message);
  const isTextMessage = message.msg_type === 'text';
  const senderFallback = getSenderFallback(message);
  const senderIsExternal = message.sender_type === 'external'
    || (!message.sender_type && isExternalWeComUserId(senderId));
  const native = Boolean(
    displayInfo && (isTextMessage || WECOM_NATIVE_DISPLAY_TYPES.has(message.msg_type)),
  );

  return {
    key: [
      message.id || message.msg_id || index,
      displayInfo?.msgid || '',
      displayInfo?.secretKey || '',
      message.send_time || '',
      message.content || '',
    ].join('-'),
    isSelf,
    rowClass: isSelf ? 'wecom-message-row wecom-message-row-self' : 'wecom-message-row',
    mainClass: isSelf ? 'wecom-message-main wecom-message-main-self' : 'wecom-message-main',
    bubbleClass: isSelf ? 'wecom-message-bubble wecom-message-bubble-self' : 'wecom-message-bubble',
    senderOpenId: senderId,
    senderNameType: senderIsExternal ? 'externalUserName' : 'userName',
    avatarType: senderIsExternal ? 'externalUserAvatar' : 'userAvatar',
    showAvatarOpenData: Boolean(senderId && message.sender_type !== 'group'),
    senderFallback,
    avatarInitial: senderFallback.charAt(0) || '?',
    msgid: displayInfo?.msgid || '',
    secretKey: displayInfo?.secretKey || '',
    native,
    previewable: WECOM_PREVIEWABLE_TYPES.has(message.msg_type),
    displayType: isTextMessage ? 'text' : 'normal',
    fallback: replaceWeChatEmoticons(message.content || `[${message.msg_type}消息]`),
    sendTime: message.send_time ? formatDateTime(message.send_time, 'MM-dd HH:mm') : '',
  };
};
